import camelCaseKeysImpl from 'camelcase-keys';
import { Dictionary } from '../../../types/globals';

export const camelCaseKeys = <T = object>(input: object, { deep }: { deep: boolean }): T =>
    (camelCaseKeysImpl(input as Dictionary<object>, { deep }) as any) as T;

export default {
    camelCaseKeys,
};

const getBase = (object: Object, accessorsArray: string[]) => {
    // eslint-disable-next-line
    return accessorsArray.reduce((acc: object, value: string, currentIndex: number, array: Array<string>) => {
        if (!value) {
            return acc;
        }

        const currentAccessObject = acc[value];
        if (currentAccessObject) {
            return currentAccessObject;
        }
        array.splice(currentIndex);
        return currentAccessObject;
    }, object);
};

const isObject = (value: any): value is object => {
    return value && typeof value === 'object' && value.constructor === Object;
};

export const get = (object: Object, accessor: string, defaultValue?: any) => {
    const accessorsArray = accessor.split(/\./);
    const result = isObject(object) ? getBase({ ...object }, accessorsArray) : undefined;
    return result === undefined || result === null ? defaultValue : result;
};

export const recursiveMerge = (left: object, right: object) => {
    const result = { ...left };
    Object.entries(right).reduce((acc, [key, rightValue]) => {
        const leftValue = acc[key];
        if (isObject(leftValue) && isObject(rightValue)) {
            const resultValue = recursiveMerge(leftValue, rightValue);
            acc[key] = resultValue;
            return acc;
        }

        acc[key] = rightValue;
        return acc;
    }, result);
    return result;
};

/** Time complexity O(nm)
 *   n = number of objects to merge
 *   m = Longest depth of NoN First Positional object
 */
export const merge = (...mergeObjects: object[]) => {
    const returnVal = mergeObjects.reduce((previousObjectsAccumulator: object, currentMergeObject: object) => {
        return recursiveMerge({ ...previousObjectsAccumulator }, { ...currentMergeObject });
    }, {});
    return returnVal;
};

const upperFirstCharacter = (string: string) => {
    return string.substr(0, 1).toUpperCase() + string.substr(1);
};

const lowerFirstCharacter = (string: string) => {
    return string.substr(0, 1).toLowerCase() + string.substr(1);
};

export const camelCase = (string: string) => {
    const returnedcamelCase = string.split('_').reduce((result: string, word: string, index: number) => {
        return result + (index ? upperFirstCharacter(word.toLowerCase()) : word.toLowerCase());
    }, '');
    return lowerFirstCharacter(returnedcamelCase);
};

type ValueType = object | Array<any> | string | number | boolean;

export const isEqualByValue = (a: ValueType, b: ValueType): boolean => {
    const typeA = typeof a;
    const typeB = typeof b;

    if (typeA !== typeB || Array.isArray(a) !== Array.isArray(b) || isObject(a) !== isObject(b)) {
        return false;
    }

    if (Array.isArray(a) && Array.isArray(b)) {
        /* eslint-disable @typescript-eslint/no-use-before-define */
        /* eslint-disable no-use-before-define */
        return compareArrays(a, b);
    }

    if (isObject(a) && isObject(b)) {
        return compareObjects(a, b);
        /* eslint-enable @typescript-eslint/no-use-before-define */
        /* eslint-enable no-use-before-define */
    }

    return a === b;
};

const compareObjects = (o1: object, o2: object) => {
    const sorted1 = Object.keys(o1).sort();
    if (sorted1.length !== Object.keys(o2).length) {
        return false;
    }

    return !sorted1.some(key => !isEqualByValue(o1[key], o2[key]));
};

const compareArrays = (arr1: Array<any>, arr2: Array<any>) => {
    if (arr1.length !== arr2.length) {
        return false;
    }

    const sorted1 = arr1.sort();
    const sorted2 = arr2.sort();

    return !sorted1.some((item, i) => !isEqualByValue(item, sorted2[i]));
};

export const getEnumKey = ({
    enumValue,
    enumObject,
}: {
    enumValue: string;
    enumObject: object | undefined;
}): string => {
    const enumKey = Object.keys(enumObject || {}).find(enumKey => enumObject?.[enumKey] === enumValue);
    return enumKey || '';
};
